/**
 * MSW (Mock Service Worker) browser integration.
 *
 * When mock data is enabled, this:
 * 1. Registers the known fixture files (names only, nothing downloaded)
 * 2. Sets up an MSW service worker that intercepts API calls
 * 3. Answers each call from its fixture JSON, fetched the first time that
 *    file is requested and cached after
 *
 * ## How mocking is toggled
 *
 * localStorage key "hubble:mock-data":
 * - absent or any value except "disabled" → mocking ON
 * - "disabled" → mocking OFF, real API calls go through
 *
 * The user menu has a toggle that calls setMockEnabled() and reloads.
 *
 * ## Service worker lifecycle gotcha
 *
 * MSW registers a service worker (mockServiceWorker.js) that persists
 * across page reloads. When the user disables mocking, we must explicitly
 * UNREGISTER the service worker — otherwise it stays active and intercepts
 * requests with "passthrough" (no handlers), which causes ERR_CACHE_MISS
 * errors in the browser.
 *
 * ## Fixture list
 *
 * The static fixture list in endpoints.tsx tells the browser which files
 * exist. In tests, fixtures are auto-discovered from the filesystem instead
 * (see vitest.snapshots.setup.ts).
 */

import { setupWorker } from "msw/browser";
import { createHandlers, createInlineHandlers } from "./handlers";
import { fixtureEndpoints } from "./endpoints";
import { INLINE_RESPONSES } from "./inlineResponses";
import { githubHandlers } from "./githubHandlers";
import { initFixtures, loadFixture } from "../Dev/fixtures";

const STORAGE_KEY = "hubble:mock-data";

let isRunning = false;

export function isMockEnabled(): boolean {
  // Mock data is off by default in the Hubble app (all environments).
  // Users can opt in via the Mock Data toggle in the user menu.
  // Storybook and tests use mock data but bypass this check.
  return localStorage.getItem(STORAGE_KEY) === "enabled";
}

export async function startMockWorker(options?: {
  force?: boolean;
}): Promise<void> {
  if (!options?.force && !isMockEnabled()) {
    // Unregister any leftover MSW service worker from a previous session.
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        if (reg.active?.scriptURL.includes("mockServiceWorker")) {
          await reg.unregister();
        }
      }
    }
    return;
  }
  if (isRunning) return;

  // Registers the fixture groups the FixtureSwitcher cycles through.
  initFixtures();

  // Inline responses must be registered alongside fixture handlers so the
  // browser (Storybook + dev mock mode) gets the same coverage as the
  // snapshot test setup. Without this, endpoints declared in INLINE_RESPONSES
  // fall through to `onUnhandledRequest: "bypass"` and hit the real network,
  // surfacing as "Network connection failed" in Storybook.
  const worker = setupWorker(
    ...createHandlers(loadFixture, undefined, fixtureEndpoints),
    ...createInlineHandlers(INLINE_RESPONSES),
    ...githubHandlers,
  );
  await worker.start({
    onUnhandledRequest: "bypass",
    quiet: true,
  });
  isRunning = true;
}
